import { Button } from "@foodpilot/foods";
import {
    Autocomplete,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { Score } from "src/types";
import { cmsAutocompleteEnterSelectProps } from "src/utils/autocomplete";

type ScoreCompanyDialogProps = {
    open: boolean;
    onClose: () => void;
    selectedScore: string[];
    onChangeScore: (value: string[]) => void;
    selectedEntity: string[];
    onChangeEntity: (value: string[]) => void;
    scores: Score[];
    onSave: () => void;
    isSaving: boolean;
};

export const ScoreCompanyDialog = (props: ScoreCompanyDialogProps) => {
    const { open, onClose, selectedScore, onChangeScore, selectedEntity, onChangeEntity, scores, onSave, isSaving } =
        props;
    const { t } = useTranslation();

    const scoreOptions = [
        { label: t("common.all", "All"), value: "" },
        ...scores.map((s) => ({ label: s.title, value: String(s.id) })),
    ];

    const entityOptions = [
        { label: t("common.all", "All"), value: "" },
        { label: t("entity.company", "Company"), value: "company" },
        { label: t("entity.product", "Product"), value: "product" },
        { label: t("entity.ingredient", "Ingredient"), value: "ingredient" },
        { label: t("entity.packaging", "Packaging"), value: "packaging" },
        { label: t("entity.supplier", "Supplier"), value: "supplier" },
        { label: t("entity.product_line", "Product line"), value: "productline" },
    ];

    const selectedScoreOptions = scoreOptions.filter((o) => selectedScore.includes(o.value));
    const selectedEntityOptions = entityOptions.filter((o) => selectedEntity.includes(o.value));

    const hasScoreSelection = selectedScoreOptions.length > 0;
    const hasEntitySelection = selectedEntityOptions.length > 0;

    return (
        <Dialog open={open} onClose={onClose}>
            <Box sx={{ width: "100vw", maxWidth: "600px" }}>
                <DialogTitle>
                    <Typography variant="body1" fontSize={18} fontWeight={100} component="div">
                        {t("settings.cms.templates.score_company.title", "Template parameters")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("settings.cms.templates.table.score", "Score")}
                        </Typography>
                        <Tooltip title={t("common.clear_all", "Clear all")}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onChangeScore([])}
                                    disabled={isSaving || !hasScoreSelection}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Autocomplete
                        {...cmsAutocompleteEnterSelectProps}
                        multiple
                        disableClearable
                        limitTags={1}
                        options={scoreOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={selectedScoreOptions}
                        onChange={(_, values) => {
                            const next = values.map((v) => v.value);
                            if (next.includes("")) {
                                onChangeScore([]);
                                return;
                            }
                            onChangeScore(next);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={""}
                                margin="dense"
                                placeholder={hasScoreSelection ? "" : t("common.all", "All")}
                            />
                        )}
                        disabled={isSaving}
                        sx={{ mt: 0 }}
                    />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("settings.cms.templates.table.entity", "Entity")}
                        </Typography>
                        <Tooltip title={t("common.clear_all", "Clear all")}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onChangeEntity([])}
                                    disabled={isSaving || !hasEntitySelection}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Autocomplete
                        {...cmsAutocompleteEnterSelectProps}
                        multiple
                        disableClearable
                        limitTags={1}
                        options={entityOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={selectedEntityOptions}
                        onChange={(_, values) => {
                            const next = values.map((v) => v.value);
                            if (next.includes("")) {
                                onChangeEntity([]);
                                return;
                            }
                            onChangeEntity(next);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={""}
                                margin="dense"
                                placeholder={hasEntitySelection ? "" : t("common.all", "All")}
                            />
                        )}
                        disabled={isSaving}
                        sx={{ mt: 0 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="ternary" onClick={onClose} disabled={isSaving}>
                        {t("settings.cms.pages.dialog.action.cancel", "Cancel")}
                    </Button>
                    <Button variant="primary" onClick={onSave} disabled={isSaving}>
                        {t("common.save", "Save")}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
